import {
  DrawerScreens,
  RootStackParamList,
} from '$tablet-app/src/app/components/PrimaryNavigator';
import Spacer from '$tablet-app/src/app/components/Spacer';
import { CalendarItemType } from '@akin-home/amplify';
import {
  CalendarItemUnion,
  useGetCalendarItem,
} from '@akin-home/calendar/combined';
import { useRecordThumbnail } from '@akin-home/images/react';
import { useGetMeal } from '@akin-home/meals/react';
import { Recipe } from '@akin-home/recipes';
import { Text } from '@akin-home/text/native';
import {
  CompositeNavigationProp,
  NavigationProp,
  useNavigation,
} from '@react-navigation/native';
import { format } from 'date-fns';
import React from 'react';
import { useWindowDimensions } from 'react-native';
import styled from 'styled-components/native';
import { ChatItemDialogBody, ChatItemHeader } from '../../ChatItem';
import { ChatCalendarItem } from '../../ChatItem/types';
import { useDismissChatItemDialog } from '../ChatItemDialog';
import { ChatItemDialogProps } from '../ChatItemDialog/ChatItemDialogProps';

type NavProps = CompositeNavigationProp<
  NavigationProp<DrawerScreens>,
  NavigationProp<RootStackParamList>
>;

export const CalendarItemChatDialog = (
  props: ChatItemDialogProps<ChatCalendarItem>
) => {
  const {
    chat_item: {
      data: { items },
    },
  } = props;

  const { navigate } = useNavigation<NavProps>();

  const onDismiss = useDismissChatItemDialog();

  const goToCalendarItem = (item: CalendarItemUnion) => {
    if (item && item.type !== 'Meal') {
      navigate('CalendarItemDetails', { id: item.id, type: item.type });
    }
    onDismiss();
  };

  const goToRecipe = (recipe: Recipe) => {
    navigate('RecipeDetails', { recipe_id: recipe.id });
    onDismiss();
  };

  const goToCalendar = () => {
    navigate('Calendar');
    onDismiss();
  };

  return (
    <>
      <ChatItemHeader
        title="calendar"
        icon="calendar-day"
        onPressOpen={goToCalendar}
        onDismiss={onDismiss}
      />
      <ChatItemDialogBody>
        {items.map((item) => (
          <React.Fragment key={item.item_id}>
            {item.type !== 'Meal' ? (
              <CalendarItemLine
                id={item.item_id}
                type={item.type}
                recurrence_id={item.recurrence_id}
                onPressOpen={goToCalendarItem}
              />
            ) : (
              // MealLine is redundant as item.type === 'Meal' is checked in ChatItemDialog.tsx
              <MealLine id={item.item_id} onPress={goToRecipe} />
            )}
          </React.Fragment>
        ))}
      </ChatItemDialogBody>
    </>
  );
};

interface ItemProps {
  id: string;
  type: CalendarItemType;
  recurrence_id?: string;
  onPressOpen(item: CalendarItemUnion): void;
}

const CalendarItemLine = (props: ItemProps) => {
  const { id, type, recurrence_id } = props;

  const { item } = useGetCalendarItem({
    type,
    id,
    recurrence_id,
  });

  const { width } = useWindowDimensions();

  const onPress = () => item && props.onPressOpen(item);

  return item ? (
    <LineContainer onPress={onPress}>
      <Left style={{ maxWidth: Math.max(300, width * 0.3) }}>
        {item.emoji ? (
          <EmojiContainer>
            <Emoji>{item.emoji}</Emoji>
          </EmojiContainer>
        ) : null}

        <DetailsView>
          <Text variant="h3" color="white" weight="bold">
            {item.title}
          </Text>

          <Text color="white" weight="semibold">
            {format(item.start, 'EEEE, d MMM')}
          </Text>
        </DetailsView>
      </Left>

      <TimeView>
        <Text variant="h3" color="white" weight="semibold">
          {format(item.start, 'h:mm a')}
        </Text>

        {'end' in item ? (
          <>
            <Text variant="h3" color="white" weight="semibold">
              {' --> '}
            </Text>
            <Text variant="h3" color="white" weight="semibold">
              {format(item.end, 'h:mm a')}
            </Text>
          </>
        ) : null}
      </TimeView>
    </LineContainer>
  ) : null;
};

const MealLine = (props: { id: string; onPress(reacipe: Recipe): void }) => {
  const { id } = props;

  const { item } = useGetMeal({
    id,
  });

  const { source } = useRecordThumbnail({ record: item });

  const onPress = () =>
    item && item.recipes[0] && props.onPress(item.recipes[0]);

  return item ? (
    <LineContainer onPress={onPress}>
      <Left>
        {source ? <Image source={source} /> : null}

        <MealTextContent>
          <Text variant="h3" color="white" weight="bold">
            {item.title}
          </Text>

          <Spacer height="15px" />

          <Text color="white" weight="semibold">
            {format(item.start, 'EEEE, d MMM')}
          </Text>
        </MealTextContent>
      </Left>
      <MealTypePill>
        <Text color="white" weight="semibold">
          {item.mealType}
        </Text>
      </MealTypePill>
    </LineContainer>
  ) : null;
};

const LineContainer = styled.TouchableOpacity`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  gap: 20px;
`;

const Left = styled.View`
  flex-direction: row;
  align-items: center;
  width: 100%;
`;

const DetailsView = styled.View`
  flex-direction: column;
  flex-shrink: 1;
  gap: 10px;
`;

const TimeView = styled.View`
  flex-direction: row;
`;

const EmojiContainer = styled.View`
  height: 48px;
  width: 48px;

  border-radius: 15px;

  background-color: rgba(5, 22, 51, 0.5);

  align-items: center;
  justify-content: center;

  margin-right: 16px;
`;

const Emoji = styled.Text`
  font-size: 28px;
`;

const Image = styled.Image`
  width: 100px;
  height: 100px;

  border-radius: 10px;
  margin-right: 21px;
`;

const MealTypePill = styled.View`
  padding: 0 16px;

  align-items: center;
  justify-content: center;

  height: 38px;

  border-color: white;
  border-width: 1px;
  border-radius: 20px;
`;

const MealTextContent = styled(Text)`
  flex: 1;
`;
